import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart, TrendingUp, Eye, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/analytics/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#003366]">Analytics Dashboard</h1>
        <p className="text-slate-600 mt-2">Track your website visitors and page views</p>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="border-l-4 border-l-[#003366]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Page Views</CardTitle>
            <Eye className="h-5 w-5 text-[#003366]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#003366]">{stats?.total_views || 0}</div>
            <p className="text-xs text-slate-500 mt-2">All time</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-[#339933]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Last 7 Days</CardTitle>
            <TrendingUp className="h-5 w-5 text-[#339933]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#339933]">{stats?.recent_views_7d || 0}</div>
            <p className="text-xs text-slate-500 mt-2">Recent activity</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-[#74C65A]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Top Pages</CardTitle>
            <BarChart className="h-5 w-5 text-[#74C65A]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#74C65A]">{stats?.top_pages?.length || 0}</div>
            <p className="text-xs text-slate-500 mt-2">Popular pages</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Pages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#003366]">Most Visited Pages</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.top_pages && stats.top_pages.length > 0 ? (
            <div className="space-y-3">
              {stats.top_pages.map((page, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-[#003366] text-white flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <span className="font-medium">{page.page}</span>
                  </div>
                  <span className="text-[#339933] font-bold">{page.views} views</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No page views recorded yet</p>
          )}
        </CardContent>
      </Card>

      {/* Daily Views */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#003366] flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Daily Views (Last 7 Days)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.daily_views && stats.daily_views.length > 0 ? (
            <div className="space-y-2">
              {stats.daily_views.map((day, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <span className="text-sm text-slate-600 w-28">{day.date}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-8 relative overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-[#003366] to-[#339933] h-full rounded-full flex items-center justify-end pr-3"
                      style={{ width: `${(day.views / Math.max(...stats.daily_views.map(d => d.views))) * 100}%` }}
                    >
                      <span className="text-white font-bold text-sm">{day.views}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No daily data available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnalytics;